"use client";

import { CopyButton } from "@/components/tool-kit/primitives";
import { RESULT_CARD, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  cssEscape,
  type EmojiEntry,
  formatCodePoints,
  htmlEntities,
  jsEscape,
} from "@/lib/tool-kit/random-fun/emoji-data";

export interface EmojiDetailStrings {
  detailName: string;
  detailGroup: string;
  detailSince: string;
  detailKeywords: string;
  detailCodePoints: string;
  detailHtml: string;
  detailCss: string;
  detailJs: string;
  copyEmoji: string;
  copyName: string;
  copyCodePoints: string;
  copied: string;
  none: string;
  sinceValue: string;
}

/** The selected emoji: its text, name and code points, with copy buttons. */
export function EmojiDetail({
  entry,
  text,
  group,
  strings,
}: {
  entry: EmojiEntry;
  /** The emoji as chosen, with any skin tone applied. */
  text: string;
  group: string;
  strings: EmojiDetailStrings;
}) {
  const rows: [string, string][] = [
    [strings.detailName, entry.name],
    [strings.detailGroup, group],
    [strings.detailSince, t(strings.sinceValue, { version: entry.since })],
    [
      strings.detailKeywords,
      entry.keywords.length > 0 ? entry.keywords.join(", ") : strings.none,
    ],
    [strings.detailCodePoints, formatCodePoints(text)],
    [strings.detailHtml, htmlEntities(text)],
    [strings.detailCss, cssEscape(text)],
    [strings.detailJs, jsEscape(text)],
  ];
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <div className="flex flex-wrap items-center gap-4">
        <span aria-hidden="true" className="text-6xl leading-none">
          {text}
        </span>
        <div className="flex flex-wrap gap-2">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyEmoji}
            text={text}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyName}
            text={entry.name}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyCodePoints}
            text={formatCodePoints(text)}
          />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <th className={`${TH} w-40`} scope="row">
                  {label}
                </th>
                <td className={`${TD} break-all font-mono text-xs`}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
